
import { format, addDays, parse, startOfWeek, addWeeks } from 'date-fns';

// Types for intent detection
interface IntentResult {
  action: 'navigate' | 'schedule' | 'query' | 'mental' | 'unknown';
  confidence: number;
  destination?: string;
  activityType?: string;
  timeFrame?: string;
  query?: string;
  mentalHealthTopic?: string;
}

// Simple pattern matching for intent detection
export const detectIntent = async (text: string): Promise<IntentResult> => {
  const input = text.toLowerCase();
  
  // Navigation intents
  if (input.includes('go to') || input.includes('open') || input.includes('show me') || input.includes('navigate')) {
    if (input.includes('analytics') || input.includes('stats') || input.includes('performance')) {
      return { action: 'navigate', confidence: 0.9, destination: '/analytics' };
    }
    
    if (input.includes('royalties') || input.includes('earnings') || input.includes('income')) {
      return { action: 'navigate', confidence: 0.9, destination: '/royalties' };
    }
    
    if (input.includes('schedule') || input.includes('calendar')) {
      return { action: 'navigate', confidence: 0.9, destination: '/calendar-scheduling' };
    }
    
    if (input.includes('release') || input.includes('publish') || input.includes('music')) {
      return { action: 'navigate', confidence: 0.9, destination: '/releases' };
    }
    
    if (input.includes('legal') || input.includes('contract') || input.includes('copyright')) {
      return { action: 'navigate', confidence: 0.9, destination: '/legal-tools' };
    }
  }
  
  // Scheduling intents
  if (input.includes('schedule') || input.includes('book') || input.includes('set up') || 
      input.includes('arrange') || input.includes('plan')) {
    
    let activityType = 'meeting';
    
    if (input.includes('recording') || input.includes('record') || input.includes('vocals')) {
      activityType = 'recording session';
    } else if (input.includes('rehearsal') || input.includes('practice')) {
      activityType = 'rehearsal';
    } else if (input.includes('meeting') || input.includes('call')) {
      activityType = 'meeting';
    } else if (input.includes('performance') || input.includes('gig') || input.includes('show')) {
      activityType = 'performance';
    }
    
    let timeFrame = '';
    
    if (input.includes('today')) {
      timeFrame = format(new Date(), 'yyyy-MM-dd');
    } else if (input.includes('tomorrow')) {
      timeFrame = format(addDays(new Date(), 1), 'yyyy-MM-dd');
    } else if (input.includes('next week')) {
      timeFrame = format(startOfWeek(addWeeks(new Date(), 1)), 'yyyy-MM-dd');
    }
    
    return { 
      action: 'schedule', 
      confidence: 0.9, 
      activityType,
      timeFrame,
      destination: '/calendar-scheduling'
    };
  }
  
  // Query intents (for stats, analytics, etc.)
  if (input.includes('how much') || input.includes('how many') || 
      input.includes('what is my') || input.includes('tell me about')) {
    
    if (input.includes('radio play') || input.includes('radio')) {
      return { 
        action: 'query', 
        confidence: 0.8,
        query: 'radio_play',
        destination: '/radio'
      };
    }
    
    if (input.includes('streams') || input.includes('plays')) {
      return { 
        action: 'query', 
        confidence: 0.8,
        query: 'streaming',
        destination: '/analytics'
      };
    }
    
    if (input.includes('royalties') || input.includes('earnings') || input.includes('income')) {
      return { 
        action: 'query', 
        confidence: 0.8,
        query: 'royalties',
        destination: '/royalties'
      };
    }
  }
  
  // Mental health and creativity support
  if (input.includes('feel') || input.includes('stress') || input.includes('anxious') ||
      input.includes('motivation') || input.includes('creative block') || 
      input.includes('stuck') || input.includes('help me with')) {
    
    let mentalHealthTopic = 'general';
    
    if (input.includes('creative block') || input.includes('inspiration') || 
        input.includes('stuck') || input.includes('idea')) {
      mentalHealthTopic = 'creative_block';
    } else if (input.includes('stress') || input.includes('anxious') || 
               input.includes('anxiety') || input.includes('overwhelm')) {
      mentalHealthTopic = 'stress';
    } else if (input.includes('motivation') || input.includes('procrastinate') || 
               input.includes('lazy') || input.includes('unmotivated')) {
      mentalHealthTopic = 'motivation';
    }
    
    return {
      action: 'mental',
      confidence: 0.85,
      mentalHealthTopic
    };
  }
  
  // Default to unknown if no clear intent is detected
  return { action: 'unknown', confidence: 0.5 };
};

// Generate AI response based on intent and input
export const suggestAction = async (text: string, intent: IntentResult): Promise<string> => {
  // Handle scheduling intents
  if (intent.action === 'schedule') {
    return `I'll help you schedule a ${intent.activityType || 'meeting'}. I've opened the scheduling assistant for you.`;
  }
  
  // Handle navigation intents
  if (intent.action === 'navigate') {
    const destination = intent.destination?.replace('/', '');
    return `I'm taking you to the ${destination} section now.`;
  }
  
  // Handle query intents
  if (intent.action === 'query') {
    switch (intent.query) {
      case 'radio_play':
        return "According to your Radio data, you received 32 plays last week across 8 different stations, a 15% increase from the previous week. Your track 'Summer Melodies' was the most played.";
      
      case 'streaming':
        return "Your music had 2,483 streams last week, with Spotify accounting for 65% of those streams. Your monthly streaming rate is up 8% compared to last month.";
      
      case 'royalties':
        return "You earned $342.18 in royalties last month. Most of this came from streaming royalties ($215.32), with the remainder from radio plays and sync licensing.";
      
      default:
        return "I'm sorry, I don't have specific information on that query. I can help you navigate to the relevant section to find out more.";
    }
  }
  
  // Handle mental health support intents
  if (intent.action === 'mental') {
    switch (intent.mentalHealthTopic) {
      case 'creative_block':
        return "I've noticed you might be experiencing a creative block. This is completely normal! Have you tried these techniques? 1) Change your environment, 2) Listen to new genres of music, 3) Collaborate with someone new, or 4) Try a completely different creative activity like painting or writing poetry to stimulate different parts of your brain.";
      
      case 'stress':
        return "I can see you're feeling stressed. Let's take a moment to breathe. Try this: take 5 deep breaths, inhaling for 4 counts and exhaling for 6. Music creation should be enjoyable, not overwhelming. Consider breaking down your projects into smaller, more manageable tasks.";
      
      case 'motivation':
        return "Finding motivation can be challenging. Remember why you started making music in the first place. Try setting small, achievable goals rather than focusing on the entire project. Even 15 minutes of focused work can build momentum. Would you like me to suggest a small task to get started?";
      
      default:
        return "I'm here to support you. Remember that your mental well-being is just as important as your music career. Taking breaks and practicing self-care actually makes you more creative and productive in the long run.";
    }
  }
  
  // Simple responses for general user inputs
  if (text.toLowerCase().includes('hello') || text.toLowerCase().includes('hi')) {
    return "Hello! How can I assist you with your music career today?";
  }
  
  if (text.toLowerCase().includes('thank')) {
    return "You're welcome! I'm always here to help you succeed in your music career.";
  }
  
  if (text.toLowerCase().includes('bye') || text.toLowerCase().includes('goodbye')) {
    return "Goodbye for now! Remember I'm always here when you need assistance.";
  }
  
  // Contextual responses for common phrases
  if (text.toLowerCase().includes('who are you') || text.toLowerCase().includes('what can you do')) {
    return "I'm your AI assistant designed to help with your music career. I can schedule events, provide insights on your statistics, offer mental wellness support, and help navigate the platform. What would you like help with today?";
  }
  
  if (text.toLowerCase().includes('how are you') || text.toLowerCase().includes('how you doing')) {
    return "I'm doing well, thanks for asking! More importantly, how are you feeling today? Anything specific you need help with in your music journey?";
  }
  
  if (text.toLowerCase().includes('joke') || text.toLowerCase().includes('funny')) {
    return "Why don't scientists trust atoms? Because they make up everything! Just like a good song needs to make up a good story. Speaking of which, how's your music coming along?";
  }
  
  if (text.toLowerCase().includes('help') || text.toLowerCase().includes('confused')) {
    return "I'm here to help! I can assist with scheduling, answer questions about your statistics, help navigate the platform, or just chat. Could you tell me more specifically what you need help with?";
  }
  
  if (text.toLowerCase().includes('feature') || text.toLowerCase().includes('can you')) {
    return "I can help you schedule events, check your analytics, navigate to different sections of the app, provide mental wellness support, and answer questions about your music career. What would you like to do today?";
  }
  
  // If no specific intent is detected but there's a question mark, provide a helpful response
  if (text.includes('?')) {
    return "That's an interesting question. I can help with scheduling, analytics, navigation, or provide mental wellness support. Could you rephrase your question or tell me more about what you're looking for?";
  }
  
  // If the text is very short (likely just trying the assistant out)
  if (text.length < 10) {
    return "Hi there! I'm your AI music assistant. I can help with scheduling, analytics, navigation, or just chat about your creative process. What would you like assistance with today?";
  }
  
  // Default response as a fallback, but more engaging than the original
  return "I understand you're trying to communicate with me, but I'm not quite sure what you're asking. I can help with scheduling events, checking your stats, navigating the app, or providing creative support. Could you try phrasing your request differently?";
};
